import { Entity as BEntity } from '@backstage/catalog-model';
import type {
  CriticalityType,
  ExposureType,
} from './criticality';

/***/
/**
 * Common functionalities for the security-asset-classification plugin.
 *
 * @packageDocumentation
 */

export type Entity = BEntity & {
  spec: BEntity['spec'] & {
    zeiss: {
      security: {
        classification: {
          criticality: CriticalityType;
          'data-properties': string;
          'information-categories': string;
          'security-classification': SecurityClassificationType;
          exposure: ExposureType;
        };
        'business-impact-analysis'?: {
          scope: BiaScope;
          'recovery-time-objective': RecoveryObjective; 
          'recovery-point-objective': RecoveryObjective; 
          rtoSource: RecoveryObjectiveSource; 
          rpoSource: RecoveryObjectiveSource;
          recoveryGap: RecoveryGap; 
          assessedAt: string; 
          nextReview: string;
        };
      };
    };
  };
};

export type DataPropertiesType =
  | 'Continuity-critical'
  | 'Service-Continuity-critical'
  | 'Platform-Continuity-critical'
  | 'Export-Control-relevant'
  | 'Data-Recovery-critical'
  | 'PII'
  | 'PHI'
  | 'Sensitive PII'
  | 'Platform-Critical'
  | 'System-Critical'
  | 'Feature-Critical';

export type InformationCategoriesType =
  | 'Information intended for Publication'
  | 'General restricted information'
  | 'Personal User Account Details'
  | 'Personal User Behavior Information'
  | 'Strategic information'
  | 'Sensitive Employee information'
  | 'Financial Data'
  | 'Business information'
  | 'Technical Product information'
  | 'Ethnic, biometric, genetic or health information'
  | 'Political or religious beliefs'
  | 'Payment Data'
  | 'Trade secrets'
  | 'Personal Identifiable Information'
  | 'Sensitive Personal Information'
  | 'Personal Health Information'
  | 'Restricted Business Information'
  | 'Financial Information'
  | 'Logs and Telemetry'
  | 'Payment Information'
  | 'Technical Specifications'
  | 'Technical Secrets'
  | 'Trade Secrets / Intellectual Property';

// used by plugin scaffolder-backend-module-security-asset-classification
export type SecurityClassificationType =
  | ('Secret' | 'Confidential' | 'Restricted' | 'Public')
  | 'Integrity-critical'
  | 'Availability-critical';
  
// Distinguishes C/I/A via prefixes and adds default options and new availability critcality.
// used by plugin scaffolder-backend-module-security-asset-classification-v2
export type SecurityClassificationTypeV2 = 
  | ('C: Secret' | 'C: Confidential' | 'C: Restricted' | 'C: Public')
  | ('I: Critical' | 'I: Default')
  | ('A: Mission-Critical' | 'A: Critical' | 'A: Default');

export type RecoveryObjective = '<24h' | '24h' | '72h' | '10d' | '30d' | 'none';

export type RecoveryObjectiveSource = 'derived' | 'segment' | 'aligned';

export type BiaScope = 'zdp' | 'customerSegment' | 'both';

export type RecoveryGap = 'no' | 'rtoGap' | 'rpoGap' | 'both' | 'unknown';

export const RECOVERY_OBJECTIVE_LABELS: Record<RecoveryObjective, string> = {
  '<24h': 'Less than 24 hours',
  '24h': '24 hours',
  '72h': '72 hours',
  '10d': '10 days',
  '30d': '30 days',
  none: 'Not defined',
};

export * from './criticality';

export {
  buildClassificationResult,
  collectInformationCategories,
} from './classification';